import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { AppIconButton, AppListPage } from '../../components/design-system';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import { SatelliteHealthIcon } from '../../branding/spatialNavIcons';
import {
  Refresh as RefreshIcon,
  CheckCircle as HealthyIcon,
  Error as UnhealthyIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import { providerTenantsService, type Tenant } from '../../services/ProviderTenantsService';
import { providerLocationOperationsService } from '../../services/ProviderLocationOperationsService';
import type {
  SiteAnchorHealthRow,
  SiteAnchorHealthSummary,
} from '@cnapse/shared/schemas.js';
import AnchorHealthSplitView from '../../components/location/AnchorHealthSplitView';

function readInitialAnchorFilters(): { tenantId: string; siteId: string } {
  if (typeof window === 'undefined') return { tenantId: '', siteId: '' };
  const params = new URLSearchParams(window.location.search);
  return {
    tenantId: params.get('tenantId') ?? '',
    siteId: params.get('siteId') ?? '',
  };
}

function SummaryCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Box sx={{ color }}>{icon}</Box>
        </Box>
        <Typography variant="h4" fontWeight="bold" sx={{ color }}>
          {value.toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function SiteAnchorHealthDashboard() {
  const initialFilters = readInitialAnchorFilters();
  const [rows, setRows] = useState<SiteAnchorHealthRow[]>([]);
  const [summary, setSummary] = useState<SiteAnchorHealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState(initialFilters.tenantId);

  const initialSiteKey = useMemo(() => {
    if (!initialFilters.siteId) return null;
    const tenant = initialFilters.tenantId || rows.find((r) => r.siteId === initialFilters.siteId)?.tenantId;
    if (!tenant) return null;
    return `${tenant}::${initialFilters.siteId}`;
  }, [initialFilters.siteId, initialFilters.tenantId, rows]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let cursor: string | undefined;
      const allRows: SiteAnchorHealthRow[] = [];
      let lastSummary: SiteAnchorHealthSummary | null = null;

      do {
        const result = await providerLocationOperationsService.getSiteAnchorHealthList({ cursor, limit: 200 });
        allRows.push(...result.data.anchors);
        lastSummary = result.data.summary;
        cursor = result.data.nextCursor ?? undefined;
      } while (cursor);

      const tenantsData = await providerTenantsService.getTenants();
      setRows(allRows);
      setSummary(lastSummary);
      setTenants(tenantsData as Tenant[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load satellite health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadData()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadData, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>(tenants.map((t) => [String(t.id), t.name]));
    return (id: string) => map.get(id) ?? null;
  }, [tenants]);

  const filteredRows = useMemo(() => {
    if (!tenantId) return rows;
    return rows.filter((r) => r.tenantId === tenantId);
  }, [rows, tenantId]);

  return (
    <AppListPage>
      {(initialFilters.tenantId || initialFilters.siteId) && (
        <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
          Deep-linked to site{' '}
          <strong>
            {initialFilters.siteId}
            {initialFilters.tenantId ? ` (${initialFilters.tenantId})` : ''}
          </strong>
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}

      {summary && (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(4, 1fr)' }}
          gap={2}
          mb={2}
          flexShrink={0}
        >
          <SummaryCard label="Assigned" value={summary.assigned} color="primary.main" icon={<SatelliteHealthIcon />} />
          <SummaryCard label="Online" value={summary.online} color="success.main" icon={<HealthyIcon />} />
          <SummaryCard label="Offline" value={summary.offline} color="error.main" icon={<UnhealthyIcon />} />
          <SummaryCard
            label="Unassigned points"
            value={summary.unassignedPoints}
            color="warning.main"
            icon={<UnknownIcon />}
          />
        </Box>
      )}

      <FormControl size="small" sx={{ mb: 2, minWidth: 220, flexShrink: 0 }}>
        <InputLabel id="anchor-health-tenant-filter" shrink>
          Tenant
        </InputLabel>
        <Select
          labelId="anchor-health-tenant-filter"
          label="Tenant"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          displayEmpty
          notched
          renderValue={(value) => {
            if (!value) {
              return <Box component="span" sx={{ color: 'text.secondary' }}>All tenants</Box>;
            }
            return tenantNameById(value) ?? value;
          }}
        >
          <MenuItem value="">All tenants</MenuItem>
          {tenants.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <AnchorHealthSplitView
          rows={filteredRows}
          rowsLoading={loading}
          showTenantOnCards={!tenantId}
          tenantNameById={tenantNameById}
          initialSiteKey={initialSiteKey}
          showDeviceMetadata
        />
      </Box>
    </AppListPage>
  );
}
